/**
 * ProofBlock.tsx - Integrated.Social
 * Homepage proof block: USP differentiator statement + three evidence cards.
 */

import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { trackSectionCtaClick } from "@/lib/analytics";

interface EvidenceCard {
  sector: string;
  sectorTag: string;
  problem: string;
  deployment: string;
  period: string;
  result: string;
  metric: string;
  metricLabel: string;
  caseStudyUrl: string;
}

const EVIDENCE_CARDS: EvidenceCard[] = [
  {
    sector: "UK FinTech Startup",
    sectorTag: "Persona Targeting + PPC",
    problem: "High CAC, wrong persona, VC funding deadline in 60 days.",
    deployment: "Persona pivot, lifestyle repositioning, 170+ A/B tests, Facebook-first mobile acquisition.",
    period: "60 days",
    result: "+350% registrations. 85% CAC reduction. Multi-million pound VC round signed off.",
    metric: "+350%",
    metricLabel: "Registrations in 60 days",
    caseStudyUrl: "/case-studies/fintech-persona-targeting/",
  },
  {
    sector: "UK B2B SaaS Company",
    sectorTag: "ABM + Agentic AI",
    problem: "14-month sales cycle, generic outreach, pipeline stalling at Tier 1 accounts.",
    deployment: "ICP rebuild, buying committee mapping across 47 accounts, intent-triggered ABM, LinkedIn Conversation Ads.",
    period: "90 days",
    result: "+312% qualified pipeline. 68% shorter sales cycle. £2.4M attributed.",
    metric: "+312%",
    metricLabel: "Pipeline in 90 days",
    caseStudyUrl: "/case-studies/b2b-saas-abm/",
  },
  {
    sector: "East African Tourism Board",
    sectorTag: "Persona-Led Paid Media",
    problem: "Generic destination messaging. ROAS stuck at 2.1x.",
    deployment: "Four traveller persona archetypes, paid search, programmatic display, paid social, A/B testing.",
    period: "12 months",
    result: "4.8x ROAS (from 2.1x). 62% lower CPL. 38% more high-value booking enquiries.",
    metric: "4.8x",
    metricLabel: "ROAS in 12 months",
    caseStudyUrl: "/case-studies/destination-marketing/",
  },
];

export default function ProofBlock() {
  return (
    <section
      id="proof"
      aria-label="Proof of deployment: client results"
      className="bg-[#0a0f1e] border-t border-white/10 py-14 lg:py-18"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* USP differentiator statement */}
        <div className="mb-10 max-w-2xl">
          <p className="text-amber-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Why Integrated.Social
          </p>
          <h2 className="text-display text-3xl lg:text-4xl text-white leading-snug mb-3">
            Proof should show the work, the commercial context and the result.
          </h2>
          <p className="text-white/55 text-sm leading-relaxed">
            Selected published case studies. Past outcomes are context, not a promise of future performance.
          </p>
        </div>

        {/* Evidence cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {EVIDENCE_CARDS.map((card) => (
            <div
              key={card.caseStudyUrl}
              className="flex flex-col bg-white/5 border border-white/10 rounded-lg p-5 hover:border-amber-400/40 transition-colors duration-200"
            >
              {/* Metric highlight */}
              <div className="mb-3">
                <span className="stat-number stat-xl text-amber-400">{card.metric}</span>
                <p className="text-white/50 text-xs mt-0.5">{card.metricLabel}</p>
              </div>

              {/* Sector + tag */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-white font-semibold text-sm">{card.sector}</span>
                <span className="text-xs text-amber-400/80 bg-amber-400/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {card.sectorTag}
                </span>
              </div>

              {/* Problem / Deployment / Result rows */}
              <dl className="flex flex-col gap-2.5 text-sm flex-1">
                <div>
                  <dt className="text-white/40 text-xs uppercase tracking-wider mb-0.5">Problem</dt>
                  <dd className="text-white/70 leading-snug">{card.problem}</dd>
                </div>
                <div>
                  <dt className="text-white/40 text-xs uppercase tracking-wider mb-0.5">Deployment</dt>
                  <dd className="text-white/65 leading-snug">{card.deployment}</dd>
                </div>
                <div>
                  <dt className="text-white/40 text-xs uppercase tracking-wider mb-0.5">Result ({card.period})</dt>
                  <dd className="text-white font-medium leading-snug">{card.result}</dd>
                </div>
              </dl>

              {/* Case study link */}
              <Link
                href={card.caseStudyUrl}
                onClick={() => trackSectionCtaClick({ section_name: "proof-block", cta_text: "Read full case study", destination: card.caseStudyUrl })}
                className="mt-4 inline-flex items-center gap-1.5 text-amber-400 text-sm font-medium hover:text-amber-300 transition-colors"
              >
                Read full case study
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>

        {/* All case studies CTA */}
        <div className="text-center">
          <Link
            href="/case-studies/"
            onClick={() => trackSectionCtaClick({ section_name: "proof-block", cta_text: "View all case studies", destination: "/case-studies/" })}
            className="inline-flex items-center gap-2 text-white/55 text-sm hover:text-white transition-colors"
          >
            View all case studies
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

      </div>
    </section>
  );
}
